/**
 * AppointmentDetailComponent
 *
 * Description: Displays detailed information about a specific appointment, allowing the user to cancel, reschedule, review, or complete the appointment.
 * Additional functionality includes joining an online appointment and showing appointment history.
 *
 * File Name: AppointmentDetailComponent.tsx
 * Date: 16-09-2024
 */

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import AppointmentHistory from "src/Components/GlobalComponents/AppointmentHistory";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { IAppointmentDetails } from "src/types/GlobalInterfaces";
import moment from "moment";
import defaultProfile from "src/assets/images/default-profile-white.png";
import SplashScreen from "src/Components/SplashScreen";
import Cookies from "js-cookie";
import { CURRENCY, customer, doctor, doctorMode } from "src/lib/constans";
import { Modal } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faXmark } from "@fortawesome/free-solid-svg-icons";
import modalIcon from "src/assets/images/complete-modal-icon.png";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@store/store";
import DeclineModalReason from "src/Components/AdminComponents/DeclineModalReason";
import AddAReviewModal from "../Customer/AddAReviewModal";
import { set } from "react-hook-form";
import CartDrawer from "../AdminComponents/CartDrawer";
import RescheduleAppointement from "../AllTabs/MyAppointment/RescheduleAppointement";
import DoctorAddReviewModal from "../Modals/DoctorAddReviewModal";
import { getIndicators } from "src/Store/redDots";

// Style for the modal container
const customeStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "rgb(255, 255, 255)",
  maxWidth: "40vw",
  width: "100%",
  padding: 12,
  borderRadius: 8,
};

// Style for the close button container
const backBtnMaain: React.CSSProperties = {
  position: "absolute",
  top: -10,
  right: -10,
  backgroundColor: "red",
  borderRadius: 100,
  width: 20,
  height: 20,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const AppointmentDetailComponent: React.FC<{
  appoinmentId: string;
  isAdmin?: boolean;
  mainContainerClass?: string;
  mainInnerContainerClass?: string;
  type?: "customer" | "doctor" | "operator" | "superAdmin";
  adjustWidth?: boolean;
  onPrescribe?: (prescribeId: string) => void;
}> = ({
  appoinmentId,
  isAdmin = false,
  mainContainerClass = "",
  mainInnerContainerClass = "",
  type = "doctor",
  adjustWidth = false,
  onPrescribe,
}) => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const navigate = useNavigate();

  const [mode] = useState(userDetails?.userRole || "");
  const [comment, setComment] = useState<string>("");
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [cancelLoader, setCancelLoader] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [completedLoader, setCompletedLoader] = useState<boolean>(false);
  const [completModal, setCompletModal] = useState<boolean>(false);
  const [data, setData] = useState<IAppointmentDetails | null>(null);
  const [reviewModal, setReviewModal] = useState<boolean>(false);
  const [roomId, setRoomId] = useState<null | string>(null);
  const [bool, setBool] = useState<boolean>(false);
  const [resceduleModal, setResceduleModal] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();

  const handleNavigateRoom = () => {
    navigate(`/online-consultation/${appoinmentId}/${roomId}/view`);
  };

  const copyToClipBoard = async (): Promise<void> => {
    try {
      const textToCopy = `${process.env.REACT_APP_DOMAIN}/online-consultation/${appoinmentId}/${roomId}/view`;

      // Check if the Clipboard API is available
      if (navigator.clipboard && window.isSecureContext) {
        // Use the modern API
        await navigator.clipboard.writeText(textToCopy);
        toast("Copied to clipboard.", { type: "success" });
      } else {
        // Fallback for older browsers (less reliable)
        const textarea = document.createElement("textarea");
        textarea.value = textToCopy;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        toast("Copied to clipboard.", { type: "success" });
      }
    } catch (err) {
      toast("Something went wrong.", { type: "error" });
    }
  };

  const cancelAppointment = async () => {
    if (!comment) {
      toast("Kindly provide a reason for canceling the appointment.", {
        type: "error",
      });
      return;
    }
    setCancelLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "post",
      path: `/reshedule/cancelled/${data?._id}/${userDetails?._id}?query=${
        isAdmin ? "admin" : "customer"
      }`,
      params: { comment },
    });
    if (res) {
      toast(res?.message || "Success", { type: "success" });
      setComment("");
      setData((prev) => (prev ? { ...prev, status: "cancelled" } : null));
      setCancelModal(false);
      setBool((prev) => !prev);
    } else {
      toast(err, { type: "error" });
    }
    setCancelLoader(false);
  };

  const createCompletRequest = async () => {
    setCompletedLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      path: `/appoiment/complete/${data?._id}`,
    });
    if (res) {
      toast(res?.message || "Success", { type: "success" });
      setCompletModal(false);
      setBool((prev) => !prev);
      setData((prev) => (prev ? { ...prev, status: "completed" } : null));
    } else {
      toast(err, { type: "error" });
    }
    setCompletedLoader(false);
  };

  const fetchInfo = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<IAppointmentDetails[]>({
      path: `/appoiment/doctor/details/${appoinmentId}?query=${
        isAdmin ? "admin" : mode
      }`,
    });
    if (res) {
      setData(res[0]);
      if (!isAdmin) {
        dispatch(getIndicators("/customer/user-indicators"));
      }
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const handleEditReview = () => {
    setReviewModal(true);
  };

  const [startCallLoader, setStartCallLoader] = useState<boolean>(false);
  const [isTimeValid, setIsTimeValid] = useState(false);

  // ************ start call ************ //
  const startCall = async () => {
    setStartCallLoader(true);
    const { res, err } = await httpRequest<{ roomId: string; message: string }>(
      { path: `/appoiment/join/meeting/${appoinmentId}` }
    );
    if (res) {
      const { message = "", roomId = "" } = res;
      if (roomId) {
        toast(message, { type: "success" });
        setRoomId(roomId);
      } else {
        toast("Room id error!", { type: "error" });
      }
    } else {
      toast(err, { type: "error" });
    }
    setStartCallLoader(false);
  };

  useEffect(() => {
    fetchInfo();
  }, [appoinmentId]);

  useEffect(() => {
    // Function to check if the selected date is today or in the future
    const isValidDate = (selectedDate: string) => {
      const currentDate = moment().format("YYYY-MM-DD"); // Start of the day ensures consistency
      const apDate = moment(selectedDate).format("YYYY-MM-DD");
      return moment(currentDate).isSameOrAfter(apDate);
    };

    // Function to check if the time is after the start time
    const isValidTime = (selectedDate: string, startTime: string) => {
      const [startHour, startMinute] = startTime.split(":").map(Number); // Extract hour and minute from startTime
      const startMoment = moment(selectedDate).set({
        hour: startHour,
        minute: startMinute,
      });
      const currentMoment = moment();

      return currentMoment.isAfter(startMoment); // Check if the current time is after the start time
    };
    // Interval to check date and time validity every minute
    const checkTimeValidity = () => {
      const appointmentDate = data?.appoinmentDate; // Assuming the date is in a valid format
      const appointmentTime = data?.startTime || ""; // Fallback to "10:00" if startTime is not provided
      if (
        appointmentDate &&
        isValidDate(appointmentDate) &&
        isValidTime(appointmentDate, appointmentTime)
      ) {
        setIsTimeValid(true);
        clearInterval(intervalId); // Stop interval when time becomes valid
      }
    };
    const intervalId = setInterval(() => {
      if (!isTimeValid) {
        checkTimeValidity();
      }
    }, 1000); // Check every second
    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [data]);

  const ModalBtnComponent: React.FC = () => {
    return (
      <div className="flex gap-5">
        <PrimaryBtn
          ButtonText="Go Back"
          onPress={() => setCancelModal(false)}
          btnExtraStyle={{
            borderRadius: "100rem",
            padding: "5px 30px",
            border: "1px solid #004540",
            backgroundColor: "#fffcf1",
            color: "#004540",
            fontSize: "14px",
          }}
        />
        <PrimaryBtn
          ButtonText="Submit"
          loader={cancelLoader}
          onPress={cancelAppointment}
          btnExtraStyle={{
            borderRadius: "100rem",
            padding: "5px 30px",
            backgroundColor: "#df0000",
            color: "#fff",
            fontSize: "14px",
          }}
        />
      </div>
    );
  };
  return (
    <>
      <Modal
        open={roomId ? true : false}
        // open={true}
        onClose={() => setRoomId(null)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div style={customeStyle}>
          <button
            id="cancel-appointment-detail-modal-close-btn"
            onClick={() => setRoomId(null)}
            style={backBtnMaain}
          >
            <FontAwesomeIcon
              className="font-size-16px text-white"
              icon={faXmark}
              size="2x"
            />
          </button>

          <div className="flex flex-col gap-3">
            <h1 className="font-size-24px font-Poppins-Medium">
              Your Meeting's ready
            </h1>
            <p className="font-size-16px font-Poppins-Medium">Copy this link</p>
            <div className="p-3 rounded-md flex items-center justify-between gap-3 bg-[#f0f0f0]">
              <p className="font-size-16px font-Poppins-Medium">{`${process.env.REACT_APP_DOMAIN}/online-consultation/${appoinmentId}/${roomId}/view`}</p>
              <FontAwesomeIcon
                role="button"
                onClick={() => copyToClipBoard()}
                icon={faCopy}
              />
            </div>
            <div className="flex justify-center">
              <PrimaryBtn
                type="button"
                onPress={handleNavigateRoom}
                ButtonText="Go to Room"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                }}
              />
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={completModal}
        onClose={() => setCompletModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="complete-confirm-modal"
      >
        <div className="modal-container">
          <button
            id="complete-appointment-detail-modal-close-btn"
            onClick={() => setCompletModal(false)}
            className="close-btn"
          >
            <FontAwesomeIcon
              className="font-size-16px text-white"
              icon={faXmark}
              size="2x"
            />
          </button>
          <div className="text-center">
            <img
              src={modalIcon}
              className="img-fluid mx-auto"
              width={60}
              title="icon"
            />
            <h1 className="py-4 font-size-22px font-Poppins-Medium">
              Are you sure you want to complete this appointment?
            </h1>
          </div>
          <div className="flex justify-center items-center gap-3">
            <PrimaryBtn
              ButtonText="No"
              onPress={() => setCompletModal(false)}
              btnExtraStyle={{
                borderRadius: "100rem",
                border: "1px solid #004540",
                backgroundColor: "#fff",
                color: "#004540",
              }}
            />
            <PrimaryBtn
              loader={completedLoader}
              onPress={createCompletRequest}
              ButtonText="Yes"
              btnExtraStyle={{
                borderRadius: "100rem",
                backgroundColor: "#004540",
              }}
            />
          </div>
        </div>
      </Modal>
      {reviewModal && (
        <DoctorAddReviewModal
          isOpen={reviewModal}
          onSuccess={() => setBool((prev) => !prev)}
          onClose={() => {
            setReviewModal(false);
          }}
          data={{
            fullName: `${data?.doctorfirstName || ""} ${
              data?.doctorlastName || ""
            }`,
            profileImage: data?.coverPhoto || "",
            specialization: data?.specialization || [],
          }}
          appoinmentId={appoinmentId}
          appointmentType="doctorAppointment"
        />
      )}
      <DeclineModalReason
        title="Please specify the reason of cancellation"
        value={comment}
        setValue={(val) => setComment(val)}
        isOpen={cancelModal}
        onClose={() => setCancelModal(false)}
        ButtonComponent={ModalBtnComponent}
      />
      {isLoader && <SplashScreen />}
      <div
        className={`table-container parent-box-appointment ${mainContainerClass}`}
      >
        <div
          className={`box-design p-0 w-[100%] relative overflow-hidden rounded-[15px] ${mainInnerContainerClass} ${
            adjustWidth ? "view-area-width-2" : ""
          }`}
        >
          {/* <div className="absolute top-0 left-0 w-full h-[35px] bg-[#00443F]" /> */}
          <div className="w-full h-[30px] lg:h-[2vw] bg-[#00443F]" />
          {data && (
            <>
              <div className="w-full relative p-6 lg:p-[1.5vw] pt-3 lg:pt-[1vw] ">
                <div className="absolute top-2 right-2 lg:top-[0.5vw] lg:right-[0.5vw]">
                  <div
                    className={`w-fit px-2 lg:px-[1.2vw] py-1 lg:py-[0.3vw] rounded-full ${
                      data?.status === "pending"
                        ? "pending-btn-appointment"
                        : data?.status === "completed"
                        ? "completed-btn-appointment"
                        : "cancel-btn-appointment"
                    }`}
                  >
                    <p className="text-red font-size-14px font-Poppins-SemiBold capitalize">
                      {data?.status || "-"}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-[20%_80%] gap-4 w-full -mt-7 lg:-mt-[2vw]">
                  <div className="w-full aspect-square rounded-md lg:rounded-[0.5vw] bg-white p-1 lg:p-[0.3vw]">
                    <img
                      alt="profile-image"
                      src={`${data.coverPhoto}`}
                      className="w-full bg-[#434343] h-full rounded-md lg:rounded-[0.5vw] object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement; // Assert the type
                        if (target) {
                          target.src = defaultProfile;
                        }
                      }}
                    />
                  </div>
                  <div className="lg:mt-[2.5vw] w-full flex justify-between">
                    <div className="w-fit pr-2 lg:pr-[0.2vw] min-w-[40%] max-w-[47%] h-fit">
                      <div className="flex flex-col gap-2">
                        <p
                          onClick={
                            mode === customer
                              ? () => navigate(`/doctor-public-profile/${data?.doctorId}/view`)
                              : () => {}
                          }
                          className={`font-Poppins-Medium ${
                            mode === customer ? "hover:underline cursor-pointer" : ""
                          } font-size-20px text-[#1C1C1C]`}
                        >
                          DR.{" "}
                          {`${data?.doctorfirstName} ${data?.doctorlastName}`}
                        </p>
                        <p className="font-Poppins-SemiBold text-[#1C1C1C] font-size-16px line-clamp-1 block w-full">
                          {data?.specialization &&
                            data?.specialization?.slice(0, 2)?.join(" | ")}
                        </p>
                        <p className="font-Poppins-Regular font-size-16px line-clamp-2 text-[#1C1C1C] block w-full">
                          {data.education && (
                            <span>
                              {data?.education?.slice(0, 1)?.join(", ")}
                            </span>
                          )}
                        </p>
                        <p className="font-Poppins-Regular font-size-16px text-[#1C1C1C]">
                          <span className="font-Poppins-Medium">Fee:</span>{" "}
                          {CURRENCY} {data?.totalAmount}
                        </p>
                      </div>
                    </div>
                    <div className="border-r border-[#ccc] h-full">

                    </div>
                    <div className="w-[47%] pl-2 lg:pl-[0.2vw] h-fit">
                      <div className="flex flex-col gap-2">
                        <p className="font-Poppins-Regular font-size-16px text-[#1C1C1C]">
                          <span className="font-Poppins-Medium">
                            Patient Name:
                          </span>{" "}
                          {data?.patientName}
                        </p>
                        <p className="font-Poppins-Regular font-size-16px text-[#1C1C1C]">
                          <span className="font-Poppins-Medium">
                            Phone Number:
                          </span>{" "}
                          {data?.phone}
                        </p>
                        <p className="font-Poppins-Regular font-size-16px text-[#1C1C1C]">
                          <span className="font-Poppins-Medium">Date:</span>{" "}
                          {moment(data?.appoinmentDate).format("MMMM DD, YYYY")}
                        </p>
                        <p className="font-Poppins-Regular font-size-16px text-[#1C1C1C]">
                          <span className="font-Poppins-Medium">Time:</span>{" "}
                          {moment(data?.startTime, "HH:mm").format("h:mm A")} -{" "}
                          {moment(data?.endTime, "HH:mm").format("h:mm A")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div
          className={`flex w-[100%] items-center justify-center gap-4 mt-[20px] mb-[10px] ${
            adjustWidth ? "view-area-width-2" : ""
          }`}
        >
          {data?.isOnlineAppoinment && mode === doctor && (
            <PrimaryBtn
              disabled={data?.status === "pending" ? false : true}
              loader={startCallLoader}
              type="submit"
              onPress={startCall}
              ButtonText="Call"
              btnExtraStyle={{
                borderRadius: "100rem",
                padding: "5px 30px",
                backgroundColor: "#004540",
                cursor: `${
                  data?.status === "pending" ? "pointer" : "not-allowed"
                }`,
              }}
            />
          )}
          {data?.status === "pending" && !isAdmin && (
            <PrimaryBtn
              type="button"
              ButtonText="Reschedule"
              btnExtraStyle={{
                borderRadius: "100rem",
                padding: "5px 30px",
                backgroundColor: "#004540",
              }}
              onPress={() => {
                if (type === "customer") {
                  setResceduleModal(true);
                } else {
                  navigate(
                    `/book-appointment/${data?.doctorId}/${data?.hospitalId}/view`,
                    {
                      state: { isEdit: data },
                    }
                  );
                }
              }}
            />
          )}
          {data?.status === "pending" && (mode === customer || isAdmin) && (
            <>
              {data?.status === "pending" && (
                <PrimaryBtn
                  type="button"
                  ButtonText="Cancel"
                  onPress={() => setCancelModal(true)}
                  btnExtraStyle={{
                    borderRadius: "100rem",
                    padding: "5px 30px",
                    backgroundColor: "#004540",
                  }}
                />
              )}
            </>
          )}
          {mode === customer && data?.status === "completed" && (
            <PrimaryBtn
              onPress={() => setReviewModal(true)}
              type="button"
              ButtonText="Review"
              btnExtraStyle={{
                borderRadius: "100rem",
                padding: "5px 30px",
                backgroundColor: "#004540",
              }}
            />
          )}
          {(isAdmin || mode === doctor) && data?.status === "pending" && (
            <PrimaryBtn
              disabled={data?.status === "pending" ? false : true}
              type="button"
              ButtonText="Complete"
              onPress={() => setCompletModal(true)}
              btnExtraStyle={{
                borderRadius: "100rem",
                padding: "5px 30px",
                backgroundColor: "#004540",
                cursor: `${
                  data?.status === "pending" ? "pointer" : "not-allowed"
                }`,
              }}
            />
          )}
          {mode === doctor && data?.status !== "cancelled" && (
            <PrimaryBtn
              type="button"
              ButtonText="Prescribe"
              onPress={() =>
                navigate(
                  `/doctor/appointment/${appoinmentId}/prescribe?type=pharmacy`
                )
              }
              btnExtraStyle={{
                borderRadius: "100rem",
                padding: "5px 30px",
                backgroundColor: "#004540",
              }}
            />
          )}
        </div>
        <div className={`${adjustWidth ? "view-area-width-2" : "w-full"}`}>
          <AppointmentHistory
            id={appoinmentId}
            reCall={bool}
            isEditModal={handleEditReview}
            onPrescribe={onPrescribe}
            appointmentData={data}
          />
        </div>
      </div>
      {type === "customer" && (
        <CartDrawer
          isOpen={resceduleModal}
          onClose={() => setResceduleModal(false)}
        >
          <RescheduleAppointement
            data={data}
            onClose={() => setResceduleModal(false)}
          />
        </CartDrawer>
      )}
    </>
  );
};

export default AppointmentDetailComponent;
